import styled from "styled-components";

/*
 * Design tokens copied from the CSM Figma set (see pages/SystemPage/SystemPage.css) rather
 * than referenced through var(--primary), for exactly the reason `SupportAssistant.styles.ts`
 * spells out: SystemPage.css is what declares those variables on :root, and it is imported by
 * `SystemPage.tsx`, which is lazy-loaded. On any screen where the user has not yet opened
 * System Settings, `var(--primary)` resolves to nothing.
 *
 * That bites harder here than it does for the chat panel. A walkthrough runs on EVERY screen in
 * the app, including the inspection reports opened from a barcode under `GuestLayout`, which
 * a user can reach in a session that never touches a manager screen at all. An unresolved
 * token would leave the spotlight card with no border and no text colour — invisible on a
 * white page — with nothing in the console to say why. Same hex values, so it still reads as
 * one app.
 */
const tokens = `
  --wt-bg: #ffffff;
  --wt-text: #36424a;
  --wt-muted: #828a8f;
  --wt-border: rgba(196, 199, 200, 0.6);
  --wt-border-strong: #c4c7c8;
  --wt-primary: #004d9e;
  --wt-accent: #0091ba;
  --wt-radius-pill: 30px;
  --wt-radius-sm: 4px;
  --wt-font: 'Noto Sans', system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
`;

/**
 * ABOVE EVERYTHING, INCLUDING THE ASSISTANT ITSELF.
 *
 * The panel is 900, and 6100 while the feedback drawer is open; the drawer's own overlay is
 * 6000 and the enlarged-screenshot view is 6200. A walkthrough is started FROM the panel and
 * has to be visible over it, so it sits above the highest of those. 6300 leaves the 6000-6299
 * band to the drawer stack, which is where the next thing of that kind will want to go.
 */
const Z = 6300;

/**
 * The dim layer.
 *
 * `pointer-events: none` IS THE FEATURE, not an optimisation. The whole point of a spotlight
 * walkthrough rather than a slide deck is that the user presses the real control on the real
 * screen — so nothing this component renders may intercept a click. It also means a mis-click
 * anywhere on the dim reaches the page underneath rather than advancing the walkthrough, which
 * is the behaviour we want: skipping a step by accident is worse than doing nothing.
 *
 * It is only painted while there is no hole yet (the second a lazy chunk may take to arrive,
 * and the quarter-second a step gets before the tour looks past it), so the user can see the
 * walkthrough is doing something rather than the screen having gone dark. Once the target is
 * measured, `Hole`'s box-shadow does the dimming and this goes transparent — two overlapping
 * 55% layers would be 80% and the page would go nearly black.
 */
export const Scrim = styled.div<{ $dim: boolean }>`
  ${tokens}

  position: fixed;
  inset: 0;
  z-index: ${Z};
  pointer-events: none;

  background: ${({ $dim }) => ($dim ? "rgba(0, 0, 0, 0.55)" : "transparent")};
  transition: background-color 120ms ease;
`;

/**
 * The hole in the dim, sitting exactly over the highlighted control.
 *
 * ⚠️ `box-shadow`, NOT `clip-path`. A clip-path on a full-page layer establishes a containing
 * block and a stacking context, which Syncfusion's popups (the card action menu, every
 * DropDownList, the grid's filter menus) then position against — they are appended to
 * `document.body` and expect the initial containing block. The spread-shadow technique needs
 * neither: this element is a 0-opacity box whose shadow paints everything outside it, so the
 * layer has no geometry of its own to fight over.
 *
 * Positioned in PAGE coordinates (`rect + window.scrollY/scrollX`), portalled into
 * `document.body` — the identical device `components/CardActionMenu` uses to stand its single
 * menu over whichever card was clicked, and for the identical reason: an absolutely positioned
 * child of `body` scrolls with the document, so viewport-relative values would drift the
 * moment the page moved under it.
 */
export const Hole = styled.div`
  position: absolute;
  z-index: ${Z};
  pointer-events: none;

  /*
   * The ring is the accent blue rather than white, so the spotlit control is the brightest,
   * most saturated thing on the screen — a white ring on a dimmed page read as grey. The
   * inner 4px glow softens it into a highlight rather than a focus outline. The hole itself is
   * padded around the control (HOLE_PAD in WalkthroughOverlay.tsx).
   */
  border-radius: 8px;
  box-shadow:
    inset 0 0 0 2px #0091ba,
    0 0 0 4px rgba(0, 145, 186, 0.35),
    0 0 0 9999px rgba(0, 0, 0, 0.55);

  transition: top 120ms ease, left 120ms ease, width 120ms ease, height 120ms ease;
`;

/**
 * The step card.
 *
 * `position: fixed` against the target's VIEWPORT rectangle rather than absolute against its
 * page rectangle, so it stays put while the page scrolls under it — and it is re-measured on
 * scroll anyway, so the two agree. It is the one thing here that takes pointer events, because
 * it carries Next and Close.
 */
export const Card = styled.div`
  ${tokens}

  position: fixed;
  z-index: ${Z + 1};
  pointer-events: auto;

  width: min(340px, calc(100vw - 24px));
  padding: 14px 16px 12px;

  background: var(--wt-bg);
  border: 1px solid var(--wt-border);
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(54, 66, 74, 0.28);

  font-family: var(--wt-font);
  color: var(--wt-text);
  text-align: left;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 500;
    line-height: 20px;
  }
`;

export const CloseButton = styled.button`
  width: 28px;
  height: 28px;
  min-width: 28px;
  margin: -4px -6px 0 0;
  border: none;
  border-radius: var(--wt-radius-sm);
  background: transparent;
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 77, 158, 0.08);
  }

  /* An <img>, never an icon font: every Syncfusion e-icons glyph renders blank app-wide,
     which is why the ribbon, the launcher and the chat panel all draw their icons this way. */
  img {
    width: 11px;
    height: 11px;
  }
`;

export const Body = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  overflow-wrap: anywhere;
`;

/**
 * The line that says something happened the user did not ask for — "Manager view has been
 * turned on so this screen can be shown".
 *
 * Quiet, but always present when it applies. Flipping a mode flag under someone silently is
 * the thing this exists to prevent; the flag is still flipped, but never without saying so.
 */
export const Notice = styled.p`
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: var(--wt-radius-sm);
  background: rgba(0, 145, 186, 0.08);
  border: 1px solid rgba(0, 145, 186, 0.25);
  color: var(--wt-text);
  font-size: 13px;
  font-weight: 300;
  line-height: 18px;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
`;

/**
 * The closing card's footer, which holds one button rather than a progress line and a button.
 *
 * Separate from `Footer` only so the button stays on the RIGHT: `space-between` with a single
 * child puts it on the left, which would move the action to the opposite corner from where the
 * user has been pressing **Next** for the whole tour.
 */
export const MessageFooter = styled(Footer)`
  justify-content: flex-end;
`;

/**
 * The left-hand column of a step's footer: the progress line, and under it the offer to ask
 * about this step.
 *
 * It exists so **Next stays in the corner it has been in for the whole tour**. `Footer` is
 * `space-between`, so a third child would land the new control in the MIDDLE and push Next
 * nowhere — but the eye has been going to the bottom-right for fifteen steps, and a control
 * that appears between the two the user already knows invites a mis-press on the one that
 * abandons the tour. Stacking it under the progress line keeps both familiar things where they
 * were and adds the new one where nothing was. Same reasoning as `MessageFooter`.
 */
export const FooterLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;

export const Progress = styled.span`
  color: var(--wt-muted);
  font-size: 12px;
  line-height: 16px;
`;

/**
 * "Ask about this step" — deliberately QUIET beside `NextButton`.
 *
 * Following the tour is the primary action and must stay the loud one; this is the escape
 * hatch for the person whose question the tour cannot answer ("so why is mine greyed out"),
 * which `utility/screenHelp.ts` names as the exact gap. Two filled pill buttons in one footer
 * would read as a choice between equals, and it is not one — most people should press Next.
 *
 * A text button rather than an outlined one because the card is 340px wide and already carries
 * a heading, a body, a progress line, a close control and Next; an outline here is one box too
 * many. `--wt-accent` rather than `--wt-primary`: the primary blue is what `NextButton` is
 * filled with, and the two sitting inches apart in the same hue would look like a rendering
 * fault rather than a hierarchy.
 */
export const AskButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: var(--wt-accent);
  font-family: var(--wt-font);
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: var(--wt-primary);
  }
`;

export const NextButton = styled.button`
  padding: 8px 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--wt-radius-pill);
  background: var(--wt-primary);
  color: #ffffff;
  font-family: var(--wt-font);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
`;

/**
 * The card shown when the walkthrough could not start, or finished having found nothing.
 *
 * Centred at the bottom of the viewport rather than beside a control, because by definition
 * there is no control to be beside.
 */
export const MessageCard = styled.div`
  ${tokens}

  position: fixed;
  z-index: ${Z + 1};
  left: 50%;
  bottom: 96px;
  transform: translateX(-50%);
  pointer-events: auto;

  width: min(380px, calc(100vw - 24px));
  padding: 14px 16px 12px;

  background: var(--wt-bg);
  border: 1px solid var(--wt-border-strong);
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(54, 66, 74, 0.28);

  font-family: var(--wt-font);
  color: var(--wt-text);
  font-size: 14px;
  font-weight: 300;
  line-height: 20px;
  text-align: left;
`;

/**
 * The class put on the highlighted element while it is spotlighted.
 *
 * A sibling of `.csm-menu-open`, which `CardActionMenu` puts on the card's own hamburger while
 * its shared popup is open — same idea, same lifetime, same obligation to be taken off again
 * when the element is superseded rather than when it is next rendered.
 *
 * It carries no styling of its own here on purpose. It exists so a screen CAN style its own
 * spotlighted control (a dark card that needs a lighter ring, say) without this component
 * knowing anything about that screen, and so the state is visible in the DOM when someone is
 * debugging a walkthrough that is pointing at the wrong thing.
 */
/** Back and Next, side by side on the right of the footer. */
export const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

/**
 * The quiet partner of Next — the app's inverted button (`.sys-btn--ghost` in SystemPage.css):
 * transparent, dark text, grey outline that darkens on hover. NOT blue text; that reads as a
 * link. Same height as Next (both 1px border + 8px padding).
 */
export const BackButton = styled.button`
  padding: 8px 18px;
  border: 1px solid var(--wt-border-strong);
  border-radius: var(--wt-radius-pill);
  background: transparent;
  color: var(--wt-text);
  font-family: var(--wt-font);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;

  transition: border-color 120ms ease;

  &:hover {
    border-color: var(--wt-text);
  }
`;

/** "CoSMo is describing this…" — muted, with a gentle pulse so it reads as in progress. */
export const Describing = styled.p`
  margin: 6px 0 0;
  color: var(--wt-muted);
  font-size: 12px;
  font-style: italic;
  line-height: 16px;

  @media (prefers-reduced-motion: no-preference) {
    animation: csm-wt-pulse 1.4s ease-in-out infinite;
  }

  @keyframes csm-wt-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }
`;

export const TARGET_CLASS = "csm-walkthrough-target";

/** The attribute every anchor is declared with. One literal, used by the registry and here. */
export const STEP_ATTRIBUTE = "data-csm-step";
